import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import toast, { Toaster } from 'react-hot-toast';
import SearchableDropdown from './common/SearchableDropdown';
import VoiceDropdown from './common/VoiceDropdown';
import { AppPageShell, AppPhoneField, Button, PageHeader, Panel } from './common/ui';
import {
  HiPhone,
  HiChatBubbleLeftRight,
  HiCpuChip,
  HiCog6Tooth,
  HiChevronDown,
} from 'react-icons/hi2';

const fieldLabelClass =
  'block text-sm font-medium text-[var(--app-ink)]';
const fieldBaseClass = 'mt-1 block w-full app-field';
const hintClass = 'text-xs text-[var(--app-muted)] mt-1.5 leading-relaxed';

const modalBackdropClass =
  'fixed inset-0 z-50 flex items-center justify-center p-4 bg-[var(--app-ink)]/45 backdrop-blur-sm';
const modalPanelClass =
  'w-full max-w-md rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface-strong)] p-6 shadow-[var(--app-popover-shadow)]';

function SendCallSection({ icon: Icon, title, description, children, className = '' }) {
  return (
    <Panel className={`p-5 sm:p-6 ${className}`.trim()}>
      <div className="mb-6 flex gap-4">
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-[var(--app-border)] bg-[var(--app-accent-soft)] text-[var(--app-accent)]"
          aria-hidden
        >
          <Icon className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold tracking-tight text-[var(--app-ink)]">
            {title}
          </h2>
          {description ? (
            <p className="mt-1 text-sm leading-relaxed text-[var(--app-muted)]">
              {description}
            </p>
          ) : null}
        </div>
      </div>
      {children}
    </Panel>
  );
}

const CreditWarningModal = ({
  isOpen,
  onClose,
  requiredCredits,
  availableCredits,
  currentDuration,
}) => {
  if (!isOpen) return null;

  return (
    <div className={modalBackdropClass} role="dialog" aria-modal="true" aria-labelledby="credit-warning-title">
      <div className={modalPanelClass}>
        <h2 id="credit-warning-title" className="text-xl font-semibold text-[var(--app-ink)]">
          Insufficient credits
        </h2>
        <div className="mt-4 space-y-4 text-[var(--app-muted)]">
          <p className="text-sm">You don&apos;t have enough credits to place this call.</p>
          <div className="rounded-xl border border-[var(--app-border)] bg-[var(--app-readonly-bg)] p-4 text-sm tabular-nums">
            <p className="flex justify-between gap-4">
              <span>Duration</span>
              <span className="font-medium text-[var(--app-ink)]">{currentDuration} min</span>
            </p>
            <p className="mt-2 flex justify-between gap-4">
              <span>Required</span>
              <span className="font-medium text-[var(--app-ink)]">{requiredCredits}</span>
            </p>
            <p className="mt-2 flex justify-between gap-4">
              <span>Available</span>
              <span className="font-medium text-[var(--app-ink)]">{availableCredits}</span>
            </p>
            <p className="mt-2 flex justify-between gap-4 border-t border-[var(--app-border)] pt-2">
              <span>Shortfall</span>
              <span className="font-semibold text-[var(--app-danger)]">
                {requiredCredits - (typeof availableCredits === 'number' ? availableCredits : 0)}
              </span>
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-[var(--app-ink)]">You can:</p>
            <ul className="mt-2 list-inside list-disc space-y-1.5 text-sm">
              <li>Lower max duration to {availableCredits} minutes or less</li>
              <li>Add credits and try again</li>
            </ul>
          </div>
        </div>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <Button href="/app/credits" className="flex-1 justify-center text-center">
            Purchase credits
          </Button>
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">
            Adjust duration
          </Button>
        </div>
      </div>
    </div>
  );
};

const SavePromptModal = ({
  isOpen,
  onClose,
  onSave,
  initialName = '',
  initialPrompt = '',
  isEditing = false,
}) => {
  const [promptName, setPromptName] = useState(initialName);
  const [promptText, setPromptText] = useState(initialPrompt);

  useEffect(() => {
    setPromptName(initialName);
    setPromptText(initialPrompt);
  }, [initialName, initialPrompt]);

  if (!isOpen) return null;

  return (
    <div className={modalBackdropClass} role="dialog" aria-modal="true" aria-labelledby="save-prompt-title">
      <div className={modalPanelClass}>
        <h2 id="save-prompt-title" className="text-xl font-semibold text-[var(--app-ink)]">
          {isEditing ? 'Edit prompt' : 'Save prompt'}
        </h2>
        <div className="mt-5 space-y-4">
          <div>
            <label className={`${fieldLabelClass} mb-2`} htmlFor="prompt-name-input">
              Name
            </label>
            <input
              id="prompt-name-input"
              type="text"
              value={promptName}
              onChange={(e) => setPromptName(e.target.value)}
              className={fieldBaseClass}
              placeholder="e.g. Insurance follow-up"
            />
          </div>
          <div>
            <label className={`${fieldLabelClass} mb-2`} htmlFor="prompt-text-input">
              Prompt text
            </label>
            <textarea
              id="prompt-text-input"
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              className={`${fieldBaseClass} min-h-[6rem]`}
              rows={4}
              placeholder="What should the agent say and do?"
            />
          </div>
        </div>
        <div className="mt-6 flex flex-row gap-3">
          <Button
            type="button"
            onClick={() => onSave(promptName, promptText)}
            className="flex-1"
            disabled={!promptName.trim() || !promptText.trim()}
          >
            {isEditing ? 'Update' : 'Save'}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

const promptTriggerClass = (selected) =>
  [
    'flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left text-sm font-medium transition-colors',
    'border-[var(--app-field-border)] bg-[var(--app-field-bg)]',
    'hover:border-[var(--app-accent)]/35 hover:bg-[var(--app-control-bg-hover)]',
    selected ? 'ring-2 ring-[var(--app-accent-ring)] border-[var(--app-accent)]/40' : '',
  ]
    .filter(Boolean)
    .join(' ');

const SearchablePromptDropdown = ({
  prompts,
  onSelect,
  onEdit,
  onDelete,
  selectedPromptId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredPrompts = prompts.filter(
    (prompt) =>
      prompt.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prompt.prompt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative min-w-[12rem]" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={promptTriggerClass(!!selectedPromptId)}
      >
        <span className="text-[var(--app-ink)]">Load saved prompt</span>
        <HiChevronDown
          className={`h-5 w-5 shrink-0 text-[var(--app-muted)] transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 z-20 mt-2 w-[min(100vw-2rem,26rem)] rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface-strong)] shadow-[var(--app-popover-shadow)]"
        >
          <div className="border-b border-[var(--app-border)] p-3">
            <input
              type="text"
              placeholder="Search prompts…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="app-field w-full rounded-xl px-3 py-2.5 text-sm"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-[min(24rem,50vh)] overflow-y-auto">
            {filteredPrompts.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-[var(--app-muted)]">
                No prompts match your search
              </div>
            ) : (
              <ul className="py-1">
                {filteredPrompts.map((prompt) => (
                  <li
                    key={prompt.id}
                    className="group border-b border-[var(--app-border)] last:border-b-0"
                  >
                    <div className="flex items-start gap-2 px-3 py-3">
                      <button
                        type="button"
                        className="min-w-0 flex-1 rounded-lg text-left transition-colors hover:bg-[var(--app-nav-hover-bg)]"
                        onClick={() => {
                          onSelect(prompt.id);
                          setIsOpen(false);
                          setSearchTerm('');
                        }}
                      >
                        <div className="font-medium text-[var(--app-ink)]">{prompt.name}</div>
                        <div className="mt-0.5 line-clamp-2 text-sm leading-relaxed text-[var(--app-muted)]">
                          {prompt.prompt}
                        </div>
                      </button>
                      <div className="flex shrink-0 gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(prompt.id);
                          }}
                          className="text-xs font-semibold text-[var(--app-accent)] hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(prompt.id);
                          }}
                          className="text-xs font-semibold text-[var(--app-danger)] hover:underline"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SendCall = () => {
  const [searchParams] = useSearchParams();
  const [dialogFlows, setDialogFlows] = useState([]);
  const [creditBalance, setCreditBalance] = useState(null);
  const [showCreditWarning, setShowCreditWarning] = useState(false);
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [showSavePromptModal, setShowSavePromptModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [tools, setTools] = useState([]);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [selectedToolIds, setSelectedToolIds] = useState([]);
  const [selectedKbIds, setSelectedKbIds] = useState([]);
  const [encryptedKey, setEncryptedKey] = useState('');
  /** Raw JSON for POST body `request_data` (pathway / task variables). Not spread into formData. */
  const [requestDataJson, setRequestDataJson] = useState('');
  const [formData, setFormData] = useState({
    phone_number: '+1',
    from: null,
    task: '',
    model: 'enhanced',
    language: 'en',
    voice: 'nat',
    dialogFlow_id: '',
    local_dialing: false,
    max_duration: 12,
    answered_by_enabled: false,
    wait_for_greeting: false,
    noise_cancellation: false,
    record: false,
    amd: false,
    interruption_threshold: 500,
    voicemail_message: '',
    temperature: null,
    transfer_phone_number: '',
    background_track: 'none',
  });
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const flowsResponse = await api.getdialogFlows();
        let pathways = [];
        if (Array.isArray(flowsResponse)) {
          pathways = flowsResponse;
        } else if (flowsResponse.pathways) {
          pathways = flowsResponse.pathways;
        } else if (flowsResponse.data && flowsResponse.data.pathways) {
          pathways = flowsResponse.data.pathways;
        }

        const mappedFlows = pathways.map((pathway) => ({
          id: pathway.id,
          name: pathway.name || `Pathway ${pathway.id}`,
        }));
        setDialogFlows(mappedFlows);

        const promptsResponse = await api.getPrompts();
        if (promptsResponse.prompts) {
          setSavedPrompts(promptsResponse.prompts);
        }

        try {
          const toolsResp = await api.getCustomTools();
          const toolContainer = toolsResp?.tools || toolsResp?.data || toolsResp?.items || toolsResp;
          const toolItems = Array.isArray(toolContainer) ? toolContainer : [];
          const normalizedTools = toolItems
            .map((t) => {
              const id = t.tool_id || t.id || t.uuid || t.identifier;
              const name = t.tool?.name || t.staging_tool?.name || t.label || id;
              return id ? { __id: id, __name: name, raw: t } : null;
            })
            .filter(Boolean);
          setTools(normalizedTools);
        } catch (e) {
          console.error('Failed to load tools', e);
          setTools([]);
        }

        try {
          const kbResp = await api.getKnowledgeBases();
          const kbContainer =
            kbResp?.vectors ||
            kbResp?.knowledgebases ||
            kbResp?.knowledgeBases ||
            kbResp?.items ||
            kbResp?.data?.vectors ||
            kbResp?.data?.items ||
            kbResp?.data ||
            kbResp;
          const kbItems = Array.isArray(kbContainer) ? kbContainer : [];
          const normalizedKBs = kbItems
            .map((kb) => {
              const id =
                kb.vectorId ||
                kb.vector_id ||
                kb.id ||
                kb.uuid ||
                kb.kb_id ||
                kb.knowledgebase_id ||
                kb.knowledge_base_id;
              const name =
                kb.name || kb.title || kb.description || kb.file_name || kb.display_name || id;
              return id ? { __id: id, __name: name, raw: kb } : null;
            })
            .filter(Boolean);
          setKnowledgeBases(normalizedKBs);
        } catch (e) {
          console.error('Failed to load knowledge bases', e);
          setKnowledgeBases([]);
        }

        const creditsResponse = await api.getCreditBalance();
        if (typeof creditsResponse.credits === 'number') {
          setCreditBalance(creditsResponse.credits);
        } else {
          console.error('Invalid credit balance format:', creditsResponse);
        }

        const contactId = searchParams.get('contactId');
        if (contactId) {
          const contactsResponse = await api.request('/contacts');
          const list = Array.isArray(contactsResponse) ? contactsResponse : [];
          const match = list.find((c) => c.id === contactId);
          if (match) {
            setSelectedContact(match);
            setFormData((prev) => ({
              ...prev,
              phone_number: match.phone_number,
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };
    fetchInitialData();
  }, [searchParams]);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setIsLoadingContacts(true);
      const response = await api.request('/contacts');
      setContacts(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setContacts([]);
    } finally {
      setIsLoadingContacts(false);
    }
  };

  const updateCreditBalance = async () => {
    try {
      const creditsResponse = await api.getCreditBalance();
      if (typeof creditsResponse.credits === 'number') {
        setCreditBalance(creditsResponse.credits);
      }
    } catch (error) {
      console.error('Failed to update credit balance:', error);
    }
  };

  const handleDialogFlowChange = (e) => {
    const dialogFlowId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      dialogFlow_id: dialogFlowId,
      task: dialogFlowId ? '' : prev.task,
    }));
  };

  const handleTaskChange = (e) => {
    const task = e.target.value;
    setFormData((prev) => ({
      ...prev,
      task,
      dialogFlow_id: task ? '' : prev.dialogFlow_id,
    }));
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return null;
    return phone.startsWith('+') ? phone : `+${phone}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (creditBalance !== null && Math.ceil(formData.max_duration) > creditBalance) {
      setShowCreditWarning(true);
      return;
    }

    const loadingToast = toast.loading('Initiating call...');
    try {
      const trimmedRequestData = requestDataJson.trim();
      let request_data;
      if (trimmedRequestData) {
        try {
          request_data = JSON.parse(trimmedRequestData);
        } catch {
          toast.error('Pathway variables must be valid JSON (an object).', { id: loadingToast });
          return;
        }
        if (
          request_data === null ||
          typeof request_data !== 'object' ||
          Array.isArray(request_data)
        ) {
          toast.error('Pathway variables must be a JSON object {...}, not an array.', {
            id: loadingToast,
          });
          return;
        }
      }

      const formattedData = {
        ...formData,
        phone_number: formatPhoneNumber(formData.phone_number),
        from: formatPhoneNumber(formData.from),
        transfer_phone_number: formatPhoneNumber(formData.transfer_phone_number),
      };

      if (request_data && Object.keys(request_data).length > 0) {
        formattedData.request_data = request_data;
      }

      const combinedTools = [...selectedToolIds, ...selectedKbIds];
      if (combinedTools.length > 0) {
        formattedData.tools = combinedTools;
      }

      const data = await api.sendCall(formattedData, {
        encryptedKey: encryptedKey?.trim() ? encryptedKey.trim() : undefined,
      });
      if (data.status === 'success') {
        toast.success('Call initiated successfully!', { id: loadingToast });
        await updateCreditBalance();
      } else {
        throw new Error(data.error || data.message || 'Failed to initiate call');
      }
    } catch (error) {
      console.error('Error initiating call:', error);

      if (
        error.message === 'Insufficient credits' ||
        error.response?.data?.error === 'Insufficient credits' ||
        error.response?.data?.message?.includes('Insufficient credits')
      ) {
        await updateCreditBalance();
        setShowCreditWarning(true);
        toast.error('Insufficient credits', { id: loadingToast });
      } else {
        toast.error(`Error initiating call: ${error.message}`, { id: loadingToast });
      }
    }
  };

  const handleMaxDurationChange = (e) => {
    const duration = Math.max(1, parseInt(e.target.value, 10) || 1);
    setFormData((prev) => ({ ...prev, max_duration: duration }));
  };

  const languages = [
    { value: 'en', label: 'English' },
    { value: 'hi', label: 'Hindi' },
    { value: 'hi-Latn', label: 'Hindi (Latin script)' },
    { value: 'el', label: 'Greek' },
    { value: 'babel-el', label: 'Greek with Auto Switch' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'it', label: 'Italian' },
    { value: 'ja', label: 'Japanese' },
    { value: 'ko', label: 'Korean' },
    { value: 'nl', label: 'Dutch' },
    { value: 'pl', label: 'Polish' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'ru', label: 'Russian' },
    { value: 'sv', label: 'Swedish' },
    { value: 'tr', label: 'Turkish' },
  ];

  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
    const formattedPhoneNumber = contact.phone_number.startsWith('+')
      ? contact.phone_number
      : `+${contact.phone_number}`;
    setFormData((prev) => ({
      ...prev,
      phone_number: formattedPhoneNumber,
    }));
  };

  const fillRequestDataFromContact = () => {
    if (!selectedContact) return;
    const next = {};
    if (selectedContact.name?.trim()) next.prospect_name = selectedContact.name.trim();
    if (selectedContact.company?.trim()) next.company_name = selectedContact.company.trim();
    if (Object.keys(next).length === 0) return;
    setRequestDataJson(JSON.stringify(next, null, 2));
  };

  const handleSavePrompt = async (name, promptText) => {
    const loadingToast = toast.loading(editingPrompt ? 'Updating prompt...' : 'Saving prompt...');
    try {
      if (editingPrompt) {
        const response = await api.updatePrompt(editingPrompt.id, {
          prompt: promptText,
          name,
        });

        if (response.status === 'success') {
          const promptsResponse = await api.getPrompts();
          if (promptsResponse.prompts) {
            setSavedPrompts(promptsResponse.prompts);
          }
          setShowSavePromptModal(false);
          setEditingPrompt(null);
          toast.success('Prompt updated successfully!', { id: loadingToast });
        }
      } else {
        const response = await api.createPrompt({
          prompt: promptText,
          name,
        });

        if (response.status === 'success') {
          const promptsResponse = await api.getPrompts();
          if (promptsResponse.prompts) {
            setSavedPrompts(promptsResponse.prompts);

            const newPrompt = promptsResponse.prompts.find(
              (p) => p.name === name && p.prompt === promptText
            );
            if (newPrompt) {
              setSelectedPrompt(newPrompt);
              setFormData((prev) => ({
                ...prev,
                task: newPrompt.prompt,
                dialogFlow_id: '',
              }));
            }
          }
          setShowSavePromptModal(false);
          toast.success('Prompt saved successfully!', { id: loadingToast });
        }
      }
    } catch (error) {
      console.error('Error saving prompt:', error);
      toast.error(`Failed to ${editingPrompt ? 'update' : 'save'} prompt: ${error.message}`, {
        id: loadingToast,
      });
    }
  };

  const handlePromptSelect = async (promptId) => {
    const loadingToast = toast.loading('Loading prompt...');
    try {
      const response = await api.getPromptDetails(promptId);
      if (response.status === 'success' && response.prompt) {
        setSelectedPrompt(response.prompt);
        setFormData((prev) => ({
          ...prev,
          task: response.prompt.prompt,
          dialogFlow_id: '',
        }));
        toast.success('Prompt loaded successfully', { id: loadingToast });
      }
    } catch (error) {
      console.error('Error loading prompt:', error);
      toast.error(`Failed to load prompt: ${error.message}`, { id: loadingToast });
    }
  };

  const handleEditPrompt = async (promptId) => {
    const loadingToast = toast.loading('Loading prompt for edit...');
    try {
      const response = await api.getPromptDetails(promptId);
      if (response.status === 'success' && response.prompt) {
        setEditingPrompt(response.prompt);
        setShowSavePromptModal(true);
        toast.success('Prompt loaded for editing', { id: loadingToast });
      }
    } catch (error) {
      console.error('Error loading prompt for edit:', error);
      toast.error(`Failed to load prompt: ${error.message}`, { id: loadingToast });
    }
  };

  const handleDeletePrompt = async (promptId) => {
    if (window.confirm('Are you sure you want to delete this prompt?')) {
      const loadingToast = toast.loading('Deleting prompt...');
      try {
        const response = await api.deletePrompt(promptId);
        if (response.status === 'success') {
          const promptsResponse = await api.getPrompts();
          if (promptsResponse.prompts) {
            setSavedPrompts(promptsResponse.prompts);
          }
          toast.success('Prompt deleted successfully!', { id: loadingToast });
        }
      } catch (error) {
        console.error('Error deleting prompt:', error);
        toast.error(`Failed to delete prompt: ${error.message}`, { id: loadingToast });
      }
    }
  };

  const handleDirectPromptUpdate = async () => {
    if (!selectedPrompt) return;

    const loadingToast = toast.loading('Updating prompt...');
    try {
      const response = await api.updatePrompt(selectedPrompt.id, {
        prompt: formData.task,
        name: selectedPrompt.name,
      });

      if (response.status === 'success') {
        const promptsResponse = await api.getPrompts();
        if (promptsResponse.prompts) {
          setSavedPrompts(promptsResponse.prompts);
        }
        toast.success('Prompt updated successfully!', { id: loadingToast });
      }
    } catch (error) {
      console.error('Error updating prompt:', error);
      toast.error(`Failed to update prompt: ${error.message}`, { id: loadingToast });
    }
  };

  const clearSelectedPrompt = () => {
    setSelectedPrompt(null);
    setFormData((prev) => ({ ...prev, task: '' }));
  };

  const handleVoiceSelect = (voice) => {
    setSelectedVoice(voice);
    setFormData((prev) => ({
      ...prev,
      voice: voice.id,
    }));
  };

  const creditOk =
    creditBalance === null || creditBalance >= Math.ceil(formData.max_duration);
  const requiredCredits = Math.ceil(formData.max_duration);

  const toggleRowClass =
    'flex cursor-pointer items-center gap-3 rounded-xl border border-transparent px-3 py-2.5 transition-colors hover:border-[var(--app-border)] hover:bg-[var(--app-readonly-bg)]';

  return (
    <AppPageShell maxWidthClass="max-w-6xl">
      <Toaster position="top-right" toastOptions={{ className: 'app-toast' }} />
      <CreditWarningModal
        isOpen={showCreditWarning}
        onClose={() => setShowCreditWarning(false)}
        requiredCredits={requiredCredits}
        availableCredits={creditBalance}
        currentDuration={formData.max_duration}
      />

      <SavePromptModal
        isOpen={showSavePromptModal}
        onClose={() => {
          setShowSavePromptModal(false);
          setEditingPrompt(null);
        }}
        onSave={handleSavePrompt}
        initialName={editingPrompt?.name || ''}
        initialPrompt={editingPrompt?.prompt || formData.task}
        isEditing={!!editingPrompt}
      />

      <PageHeader
        eyebrow="Outbound"
        title="Send call"
        description="Configure one AI phone call: who you dial, what the agent should do, and how it should sound."
        aside={
          creditBalance !== null ? (
            <div className="w-full max-w-[14rem] rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface-strong)] px-5 py-4 shadow-[var(--app-panel-shadow)]">
              <p className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-[var(--app-muted)]">
                Credit balance
              </p>
              <p className="mt-1 text-3xl font-bold tabular-nums tracking-tight text-[var(--app-accent)]">
                {creditBalance}
              </p>
              <p className="mt-1 text-sm text-[var(--app-muted)]">
                ~{requiredCredits} needed for {formData.max_duration} min
              </p>
            </div>
          ) : null
        }
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        <SendCallSection
          icon={HiPhone}
          title="Destination & limits"
          description="Who you are calling, optional caller ID, and how long the session may run."
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
            <div className="sm:col-span-2">
              <label className={fieldLabelClass} htmlFor="sendcall-contact">
                Phone number *
              </label>
              <div className="mt-1">
                <SearchableDropdown
                  options={contacts}
                  onSelect={handleContactSelect}
                  selectedOption={selectedContact}
                  placeholder={isLoadingContacts ? 'Loading contacts…' : 'Select a contact or type below'}
                  searchPlaceholder="Search contacts…"
                  displayKey="name"
                  secondaryDisplayKey="phone_number"
                  className="mb-3"
                  renderOption={(contact) => (
                    <div>
                      <div className="text-base font-medium text-[var(--app-ink)]">{contact.name}</div>
                      <div className="text-sm text-[var(--app-muted)]">{contact.phone_number}</div>
                      {contact.email ? (
                        <div className="text-sm text-[var(--app-muted)]/80">{contact.email}</div>
                      ) : null}
                      {contact.company ? (
                        <div className="text-sm text-[var(--app-muted)]/80">{contact.company}</div>
                      ) : null}
                    </div>
                  )}
                />
                <AppPhoneField
                  id="sendcall-contact"
                  country="us"
                  value={formData.phone_number}
                  onChange={(phone) => {
                    setFormData((prev) => ({ ...prev, phone_number: phone }));
                    setSelectedContact(null);
                  }}
                  inputClass="mt-1 block"
                  required
                />
              </div>
            </div>

            <div>
              <label className={`${fieldLabelClass} mb-2`} htmlFor="sendcall-from">
                From number (optional)
              </label>
              <AppPhoneField
                id="sendcall-from"
                country="us"
                value={formData.from || ''}
                onChange={(phone) => setFormData((prev) => ({ ...prev, from: phone }))}
                inputClass="mt-1 block"
              />
            </div>

            <div>
              <label className={`${fieldLabelClass} mb-2`} htmlFor="sendcall-duration">
                Max duration (minutes) *
              </label>
              <input
                id="sendcall-duration"
                type="number"
                value={formData.max_duration}
                onChange={handleMaxDurationChange}
                className={fieldBaseClass}
                min={1}
                max={60}
                required
              />
              <p className={hintClass}>
                This call uses {requiredCredits} credit{requiredCredits !== 1 ? 's' : ''}.
                {creditBalance !== null && (
                  <span
                    className={
                      creditOk
                        ? ' text-emerald-600 dark:text-emerald-400'
                        : ' font-medium text-[var(--app-danger)]'
                    }
                  >
                    {creditOk
                      ? ` You have ${creditBalance} available.`
                      : ` You need ${requiredCredits} but have ${creditBalance}.`}
                  </span>
                )}
              </p>
            </div>

            <div className="sm:col-span-2">
              <label className={`${fieldLabelClass} mb-2`} htmlFor="sendcall-transfer">
                Transfer number (optional)
              </label>
              <AppPhoneField
                id="sendcall-transfer"
                country="us"
                value={formData.transfer_phone_number || ''}
                onChange={(phone) =>
                  setFormData((prev) => ({ ...prev, transfer_phone_number: phone }))
                }
                inputClass="mt-1 block"
                placeholder="Transfer target"
              />
              <p className={hintClass}>If the agent transfers the call, it will use this number.</p>
            </div>
          </div>
        </SendCallSection>

        <SendCallSection
          icon={HiChatBubbleLeftRight}
          title="Script & context"
          description="Use a saved dialog flow or a one-off task. Attach tools and knowledge when you need structured actions or retrieval."
        >
          <div className="space-y-5">
            <div>
              <label className={`${fieldLabelClass} mb-2`} htmlFor="sendcall-flow">
                Dialog flow (optional)
              </label>
              <select
                id="sendcall-flow"
                value={formData.dialogFlow_id}
                onChange={handleDialogFlowChange}
                disabled={!!formData.task}
                className={`${fieldBaseClass} ${formData.task ? 'cursor-not-allowed opacity-70' : ''}`}
              >
                <option value="">None — use task below</option>
                {dialogFlows.length > 0 ? (
                  dialogFlows.map((flow) => (
                    <option key={flow.id} value={flow.id}>
                      {flow.name}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    Loading dialog flows…
                  </option>
                )}
              </select>
              <p className={hintClass}>
                {formData.task
                  ? 'Clear the task to pick a flow, or remove the flow to write a task.'
                  : 'Choose a flow or enter a task — not both.'}
              </p>
            </div>

            <div>
              <div className="mb-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <label className={fieldLabelClass} htmlFor="sendcall-task">
                  Task / prompt {!formData.dialogFlow_id && '(required)'}
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  {selectedPrompt ? (
                    <div className="inline-flex flex-wrap items-center gap-2 rounded-xl border border-[var(--app-accent)]/30 bg-[var(--app-accent-soft)] px-3 py-1.5 text-sm">
                      <span className="text-[var(--app-ink)]">
                        <span className="text-[var(--app-muted)]">Editing</span>{' '}
                        <span className="font-medium">{selectedPrompt.name}</span>
                      </span>
                      <button
                        type="button"
                        onClick={handleDirectPromptUpdate}
                        className="font-semibold text-[var(--app-accent)] hover:underline"
                      >
                        Save changes
                      </button>
                      <button
                        type="button"
                        onClick={clearSelectedPrompt}
                        className="text-[var(--app-muted)] hover:text-[var(--app-ink)]"
                        aria-label="Clear selected prompt"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    formData.task && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditingPrompt(null);
                          setShowSavePromptModal(true);
                        }}
                        className="text-sm font-semibold text-[var(--app-accent)] hover:underline"
                      >
                        Save as new prompt
                      </button>
                    )
                  )}
                  {savedPrompts.length > 0 && (
                    <SearchablePromptDropdown
                      prompts={savedPrompts}
                      onSelect={handlePromptSelect}
                      onEdit={handleEditPrompt}
                      onDelete={handleDeletePrompt}
                      selectedPromptId={selectedPrompt?.id}
                    />
                  )}
                </div>
              </div>
              <textarea
                id="sendcall-task"
                value={formData.task}
                onChange={handleTaskChange}
                disabled={!!formData.dialogFlow_id}
                className={`${fieldBaseClass} min-h-[7rem] resize-y ${formData.dialogFlow_id ? 'cursor-not-allowed opacity-70' : ''}`}
                rows={4}
                placeholder={
                  formData.dialogFlow_id
                    ? 'Disabled while a dialog flow is selected'
                    : 'Instructions for the agent: goal, tone, constraints, and what to collect.'
                }
                required={!formData.dialogFlow_id}
              />
            </div>

            <div>
              <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                <label className={fieldLabelClass} htmlFor="sendcall-request-data">
                  Pathway / prompt variables (optional)
                </label>
                {selectedContact ? (
                  <button
                    type="button"
                    onClick={fillRequestDataFromContact}
                    className="text-left text-sm font-semibold text-[var(--app-accent)] hover:underline sm:text-right"
                  >
                    Fill prospect_name & company_name from contact
                  </button>
                ) : null}
              </div>
              <textarea
                id="sendcall-request-data"
                value={requestDataJson}
                onChange={(e) => setRequestDataJson(e.target.value)}
                className={`${fieldBaseClass} min-h-[6rem] resize-y font-mono text-sm`}
                spellCheck={false}
                placeholder={`{\n  "company_name": "Acme Inc",\n  "agent_name": "Sam",\n  "prospect_name": "Jordan"\n}`}
              />
              <p className={hintClass}>
                JSON object only. Sent as{' '}
                <code className="rounded bg-[var(--app-readonly-bg)] px-1 py-0.5 text-xs">
                  request_data
                </code>{' '}
                to your backend; keys match placeholders like{' '}
                <code className="rounded bg-[var(--app-readonly-bg)] px-1 py-0.5 text-xs">
                  {'{{company_name}}'}
                </code>{' '}
                in dialog flows or tasks. Leave empty if unused.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={`${fieldLabelClass} mb-2`}>Tools (optional)</label>
                <div className="max-h-56 overflow-y-auto rounded-2xl border border-[var(--app-field-border)] bg-[var(--app-field-bg)] p-2 shadow-inner">
                  {tools && tools.length > 0 ? (
                    tools.map((tool) => {
                      const id = tool.__id;
                      const name = tool.__name;
                      const checked = selectedToolIds.includes(id);
                      return (
                        <label key={id} className={toggleRowClass}>
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-[var(--app-field-border)] text-[var(--app-accent)] focus:ring-[var(--app-accent-ring)]"
                            checked={checked}
                            onChange={(e) => {
                              setSelectedToolIds((prev) =>
                                e.target.checked ? [...prev, id] : prev.filter((x) => x !== id)
                              );
                            }}
                          />
                          <span className="text-sm text-[var(--app-ink)]">{name}</span>
                        </label>
                      );
                    })
                  ) : (
                    <div className="px-2 py-6 text-center text-sm text-[var(--app-muted)]">
                      No custom tools yet
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className={`${fieldLabelClass} mb-2`}>Knowledge bases (optional)</label>
                <div className="max-h-56 overflow-y-auto rounded-2xl border border-[var(--app-field-border)] bg-[var(--app-field-bg)] p-2 shadow-inner">
                  {knowledgeBases && knowledgeBases.length > 0 ? (
                    knowledgeBases.map((kb) => {
                      const id = kb.__id;
                      const name = kb.__name;
                      const checked = selectedKbIds.includes(id);
                      return (
                        <label key={id} className={toggleRowClass}>
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-[var(--app-field-border)] text-[var(--app-accent)] focus:ring-[var(--app-accent-ring)]"
                            checked={checked}
                            onChange={(e) => {
                              setSelectedKbIds((prev) =>
                                e.target.checked ? [...prev, id] : prev.filter((x) => x !== id)
                              );
                            }}
                          />
                          <span className="text-sm text-[var(--app-ink)]">{name}</span>
                        </label>
                      );
                    })
                  ) : (
                    <div className="px-2 py-6 text-center text-sm text-[var(--app-muted)]">
                      No knowledge bases yet
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </SendCallSection>

        <SendCallSection
          icon={HiCpuChip}
          title="Voice & model"
          description="Language, reasoning model, voice, and optional creativity and ambience."
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
            <div>
              <label className={`${fieldLabelClass} mb-2`} htmlFor="sendcall-model">
                Model
              </label>
              <select
                id="sendcall-model"
                value={formData.model}
                onChange={(e) => setFormData((prev) => ({ ...prev, model: e.target.value }))}
                className={fieldBaseClass}
              >
                <option value="base">Base</option>
                <option value="enhanced">Enhanced</option>
                <option value="turbo">Turbo</option>
              </select>
            </div>
            <div>
              <label className={`${fieldLabelClass} mb-2`} htmlFor="sendcall-lang">
                Language
              </label>
              <select
                id="sendcall-lang"
                value={formData.language}
                onChange={(e) => setFormData((prev) => ({ ...prev, language: e.target.value }))}
                className={fieldBaseClass}
              >
                {languages.map((lang) => (
                  <option key={lang.value} value={lang.value}>
                    {lang.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={`${fieldLabelClass} mb-2`}>Voice</label>
              <VoiceDropdown onSelect={handleVoiceSelect} selectedVoice={selectedVoice} />
            </div>
            <div>
              <label className={`${fieldLabelClass} mb-2`} htmlFor="sendcall-temp">
                Temperature (optional)
              </label>
              <input
                id="sendcall-temp"
                type="number"
                value={formData.temperature ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    temperature: parseFloat(e.target.value) || null,
                  }))
                }
                className={fieldBaseClass}
                step={0.1}
                min={0}
                max={2}
                placeholder="0 – 2, default from agent"
              />
            </div>
            <div>
              <label className={`${fieldLabelClass} mb-2`} htmlFor="sendcall-bg">
                Background track
              </label>
              <select
                id="sendcall-bg"
                value={formData.background_track}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, background_track: e.target.value }))
                }
                className={fieldBaseClass}
              >
                <option value="none">None</option>
                <option value="office">Office ambience</option>
                <option value="restaurant">Restaurant</option>
                <option value="street">Street</option>
              </select>
            </div>
          </div>
        </SendCallSection>

        <Panel className="overflow-hidden p-0">
          <button
            type="button"
            onClick={() => setAdvancedOpen((o) => !o)}
            className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-[var(--app-readonly-bg)] sm:px-6 sm:py-5"
            aria-expanded={advancedOpen}
          >
            <div className="flex min-w-0 items-center gap-4">
              <div
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-[var(--app-border)] bg-[var(--app-accent-soft)] text-[var(--app-accent)]"
                aria-hidden
              >
                <HiCog6Tooth className="h-6 w-6" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold tracking-tight text-[var(--app-ink)]">
                  Advanced options
                </h2>
                <p className="mt-0.5 text-sm text-[var(--app-muted)]">
                  BYOT key, interruption timing, recording, detection flags, voicemail copy.
                </p>
              </div>
            </div>
            <HiChevronDown
              className={`h-6 w-6 shrink-0 text-[var(--app-muted)] transition-transform ${advancedOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {advancedOpen ? (
            <div className="space-y-6 border-t border-[var(--app-border)] px-5 py-5 sm:px-6 sm:py-6">
              <div>
                <label className={`${fieldLabelClass} mb-2`} htmlFor="sendcall-byot">
                  Encrypted key (BYOT, optional)
                </label>
                <input
                  id="sendcall-byot"
                  type="text"
                  value={encryptedKey}
                  onChange={(e) => setEncryptedKey(e.target.value)}
                  className={fieldBaseClass}
                  placeholder="Paste encrypted_key if using BYOT"
                />
                <p className={hintClass}>Sent in headers with the call request when provided.</p>
              </div>
              <div>
                <label className={`${fieldLabelClass} mb-2`} htmlFor="sendcall-interrupt">
                  Interruption threshold (ms)
                </label>
                <input
                  id="sendcall-interrupt"
                  type="number"
                  value={formData.interruption_threshold}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      interruption_threshold: parseInt(e.target.value, 10),
                    }))
                  }
                  className={fieldBaseClass}
                  min={0}
                  max={2000}
                />
              </div>
              <div>
                <p className={`${fieldLabelClass} mb-2`}>Call behavior</p>
                <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3">
                  {[
                    {
                      key: 'record',
                      label: 'Record call',
                      checked: formData.record,
                      onChange: (v) => setFormData((p) => ({ ...p, record: v })),
                    },
                    {
                      key: 'amd',
                      label: 'AMD (answering machine detection)',
                      checked: formData.amd,
                      onChange: (v) => setFormData((p) => ({ ...p, amd: v })),
                    },
                    {
                      key: 'wait_for_greeting',
                      label: 'Wait for greeting',
                      checked: formData.wait_for_greeting,
                      onChange: (v) => setFormData((p) => ({ ...p, wait_for_greeting: v })),
                    },
                    {
                      key: 'noise_cancellation',
                      label: 'Noise cancellation',
                      checked: formData.noise_cancellation,
                      onChange: (v) => setFormData((p) => ({ ...p, noise_cancellation: v })),
                    },
                    {
                      key: 'local_dialing',
                      label: 'Local dialing',
                      checked: formData.local_dialing,
                      onChange: (v) => setFormData((p) => ({ ...p, local_dialing: v })),
                    },
                    {
                      key: 'answered_by_enabled',
                      label: 'Answered-by detection',
                      checked: formData.answered_by_enabled,
                      onChange: (v) => setFormData((p) => ({ ...p, answered_by_enabled: v })),
                    },
                  ].map(({ key, label, checked, onChange }) => (
                    <label key={key} className={toggleRowClass}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-[var(--app-field-border)] text-[var(--app-accent)] focus:ring-[var(--app-accent-ring)]"
                        checked={checked}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                      <span className="text-sm text-[var(--app-ink)]">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className={`${fieldLabelClass} mb-2`} htmlFor="sendcall-vm">
                  Voicemail message (optional)
                </label>
                <textarea
                  id="sendcall-vm"
                  value={formData.voicemail_message}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, voicemail_message: e.target.value }))
                  }
                  className={`${fieldBaseClass} min-h-[6rem] resize-y`}
                  placeholder="If voicemail is detected, the agent can leave this message."
                />
              </div>
            </div>
          ) : null}
        </Panel>

        <div className="sticky bottom-4 z-10">
          {creditBalance !== null && !creditOk ? (
            <Panel className="border-[var(--app-alert-error-border)] bg-[var(--app-alert-error-bg)] p-5 sm:p-6">
              <h3 className="text-base font-semibold text-[var(--app-alert-error-fg)]">
                Not enough credits
              </h3>
              <p className="mt-2 text-sm text-[var(--app-alert-error-fg)] opacity-90">
                This call needs {requiredCredits} credits; your balance is {creditBalance}.
              </p>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <Button href="/app/credits" className="flex-1 justify-center text-center">
                  Add credits
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      max_duration: Math.max(1, Math.floor(creditBalance)),
                    }))
                  }
                  className="flex-1"
                >
                  Set duration to {Math.max(1, Math.floor(creditBalance))} min
                </Button>
              </div>
            </Panel>
          ) : (
            <Panel className="flex flex-col gap-4 border-[var(--app-border)] bg-[var(--app-surface-strong)] p-4 shadow-[var(--app-popover-shadow)] backdrop-blur-md sm:flex-row sm:items-center sm:justify-between sm:p-5">
              <p className="text-sm text-[var(--app-muted)]">
                Ready to dial{' '}
                <span className="font-medium text-[var(--app-ink)]">
                  {formData.phone_number || '…'}
                </span>
                {formData.dialogFlow_id
                  ? ' using your dialog flow.'
                  : formData.task
                    ? ' with your task.'
                    : '.'}
              </p>
              <Button type="submit" size="lg" className="w-full shrink-0 sm:w-auto sm:min-w-[12rem]">
                Send call
              </Button>
            </Panel>
          )}
        </div>
      </form>
    </AppPageShell>
  );
};

export default SendCall;
